import { eventRegistrationPolicy } from "@/src/models/event.model";
import { useTheme } from "@/src/styles/theme-provider";
import { format } from "date-fns";
import { Calendar, Clock } from "iconsax-react-nativejs";
import { useTranslation } from "react-i18next";
import { Text, View } from "react-native";
import { ProgressBar } from "react-native-paper";
import { SectionCard } from "./event-detail-section-card";

type EventDetailInfoProps = Readonly<{
  description?: string;
  startTime: string;
  endTime: string;
  /** Attendees + their guests — capacity is measured in seats, not RSVP rows. */
  seatsTaken: number;
  capacity: number;
  registrationPolicy: string;
}>;

const EventDetailInfo = ({
  description,
  startTime,
  endTime,
  seatsTaken,
  capacity,
  registrationPolicy,
}: EventDetailInfoProps) => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();

  const startDate = new Date(startTime);
  const endDate = new Date(endTime);
  const formattedDate = format(startDate, "EEE, MMM d, yyyy");
  const formattedStart = format(startDate, "h:mm a");
  const formattedEnd = format(endDate, "h:mm a");
  // CR-B2-07: show viewer's local timezone so the time is unambiguous
  const tzAbbr =
    new Intl.DateTimeFormat("en-US", { timeZoneName: "short" })
      .formatToParts(new Date())
      .find((p) => p.type === "timeZoneName")?.value ?? "";

  // Guard `capacity > 0`: a Required event with no seat limit (legacy rows, or
  // an API null) would otherwise divide by zero and render as permanently
  // "Full" / "100% spots filled" / "N / 0".
  const showCapacity =
    registrationPolicy === eventRegistrationPolicy.REQUIRED && capacity > 0;
  const capacityPercent = showCapacity
    ? Math.min(seatsTaken / capacity, 1)
    : 0;
  const capacityFull = capacityPercent >= 1;
  const capacityAlmostFull = capacityPercent >= 0.8;
  const capacityBarColor = capacityFull
    ? themeVars["--color-crimson-default"] as string
    : capacityAlmostFull
      ? themeVars["--color-primary-2"] as string
      : themeVars["--color-primary-default"] as string;

  const capacityStatusLabel = capacityFull
    ? t("LABEL_FULL")
    : capacityAlmostFull
      ? t("LABEL_ALMOST_FULL")
      : null;

  return (
    <SectionCard>
      {/* Capacity + date in one row */}
      {showCapacity && (
        <View className="flex-row gap-4 mb-4">
          {/* Capacity — left */}
          <View className="flex-1 gap-2">
            <View className="flex-row items-center justify-between gap-2 flex-wrap">
              <View className="flex-row items-center gap-1.5 shrink-0">
                <Text className="text-base font-semibold text-onyx">
                  {t("LABEL_CAPACITY")}
                </Text>
                {capacityStatusLabel && (
                  <View
                    className={`px-1.5 py-0.5 rounded-full ${capacityFull ? "text-red-400" : "text-slate"}`}
                  >
                    <Text
                      className={`text-xs font-medium ${capacityFull ? "text-red-400" : "text-slate"}`}
                    >
                      {capacityStatusLabel}
                    </Text>
                  </View>
                )}
              </View>
              <Text className="text-xs font-bold shrink-0 text-onyx">
                {seatsTaken} / {capacity}
              </Text>
            </View>
            <View className="h-1.5 rounded-full overflow-hidden">
              <ProgressBar
                progress={capacityPercent}
                color={capacityBarColor}
                style={{ flex: 1, backgroundColor: `${capacityBarColor}20` }}
              />
            </View>
            <Text className="text-xs text-onyx">
              {Math.round(capacityPercent * 100)}% {t("LABEL_SPOTS_FILLED")}
            </Text>
          </View>

          {/* Vertical divider */}
          <View className="w-px bg-ash-3" />

          {/* Date — right */}
          <View className="flex-1 gap-1">
            <View className="flex-row items-center gap-1.5">
              <Text className="text-base font-semibold text-onyx">
                {t("LABEL_DATE_AND_TIME")}
              </Text>
            </View>
            <Text className="text-xs text-onyx">{formattedDate}</Text>
            <View className="flex-row items-center gap-1">
              <Clock size={18} color="gray" />
              <Text className="text-xs text-onyx">
                {formattedStart} – {formattedEnd}
                {tzAbbr ? ` (${tzAbbr})` : ""}
              </Text>
            </View>
          </View>
        </View>
      )}

      {/* Divider between capacity/date row and description */}
      {showCapacity && description && (
        <View className="h-px bg-ash -mx-4 mb-4" />
      )}

      {/* Description */}
      {description && (
        <View className="gap-1.5">
          <Text className="text-base font-semibold text-onyx">
            {t("LABEL_ABOUT_THIS_EVENT")}
          </Text>
          <Text className="text-sm leading-6 text-onyx">{description}</Text>
        </View>
      )}

      {/* Date only (no capacity) */}
      {!showCapacity && (
        <View className="flex-row gap-3 mt-4">
          <View className="w-10 h-10 rounded-xl items-center justify-center shrink-0 bg-primary-light">
            {/* "primary" is not a colour value — iconsax got an unresolvable
                string and painted nothing, leaving the bg-primary-light tile
                looking like an empty blue box. */}
            <Calendar
              size={20}
              color={themeVars["--color-primary-default"] as string}
            />
          </View>
          <View className="gap-0.5 flex-1">
            <Text className="text-sm font-semibold text-onyx">
              {t("LABEL_DATE_AND_TIME")}
            </Text>
            <Text className="text-sm text-onyx">{formattedDate}</Text>
            <View className="flex-row items-center gap-1 ">
              <Clock size={12} color="gray" />
              <Text className="text-sm text-onyx">
                {formattedStart} – {formattedEnd}
                {tzAbbr ? ` (${tzAbbr})` : ""}
              </Text>
            </View>
          </View>
        </View>
      )}
    </SectionCard>
  );
};

export default EventDetailInfo;
